import React, { useEffect, useState } from "react";
import { toast } from "sonner";

const btn = "font-display tracking-[0.18em] text-[11px] uppercase px-3 py-2 border transition-colors disabled:opacity-50";
const err = (e) => { const d = e.response?.data?.detail; return typeof d === "string" ? d : Array.isArray(d) ? d.map((x) => x.msg).join(" ") : "Noe gikk galt."; };
const fmt = (iso) => iso ? new Date(iso).toLocaleString("nb-NO", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "";

function Versions({ items, onLoad }) {
  return (
    <div data-testid="prompt-versions" className="border border-edge p-4">
      <div className="font-display tracking-[0.18em] text-[11px] uppercase text-coolwhite">Tidligere versjoner</div>
      {items.length === 0 && <p className="mt-2 font-term text-[12px] text-dim">// ingen ennå — lagres automatisk når du endrer</p>}
      <ul className="mt-2 divide-y divide-edge">
        {items.map((v, i) => (
          <li key={v.id} className="py-2 flex items-center justify-between gap-3">
            <span className="font-term text-[12px] text-coolwhite/85">{fmt(v.saved_at)}{v.was_default ? " · standard" : ""} · {v.text.length} tegn</span>
            <button data-testid={`prompt-version-load-${i}`} onClick={() => onLoad(v.text)} className={`${btn} border-edge text-coolwhite hover:border-l3 hover:text-l3`}>hent inn</button>
          </li>
        ))}
      </ul>
    </div>
  );
}

export const PromptTab = ({ ax }) => {
  const [lang, setLang] = useState("no");
  const [data, setData] = useState(null);
  const [text, setText] = useState("");
  const [versions, setVersions] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async (l = lang) => {
    try {
      const [{ data: d }, { data: v }] = await Promise.all([ax.get("/admin/prompt"), ax.get(`/admin/prompt/${l}/versions`)]);
      setData(d); setText(d[l].text); setVersions(v);
    } catch (e) { toast.error(err(e)); }
  };
  useEffect(() => { load(lang); }, [lang]); // eslint-disable-line react-hooks/exhaustive-deps

  const cur = data?.[lang];
  const dirty = cur && text !== cur.text;
  const run = async (fn, ok) => {
    setBusy(true);
    try { const { data: r } = await fn(); toast.success(r.changed === false ? "Ingen endringer." : ok); await load(lang); }
    catch (e) { toast.error(err(e)); } finally { setBusy(false); }
  };
  const save = () => run(() => ax.put(`/admin/prompt/${lang}`, { text }), "Lagret — gjelder fra neste melding i alle samtaler.");
  const reset = () => window.confirm("Tilbakestille til standardinstruksen? Nåværende tekst lagres som tidligere versjon.") &&
    run(() => ax.post(`/admin/prompt/${lang}/reset`), "Tilbakestilt til standard.");
  const switchLang = (l) => { if (!dirty || window.confirm("Du har ulagrede endringer. Bytte språk likevel?")) setLang(l); };

  return (
    <div data-testid="prompt-tab" className="space-y-5">
      <div>
        <h2 className="font-display tracking-[0.18em] text-[13px] uppercase text-coolwhite">Tvillingens instruks</h2>
        <p className="mt-1 font-term text-[13px] text-dim max-w-[900px]">Dette er hele instruksen tvillingen følger: profil, stemme, regler og kontaktinfo. Endringer gjelder fra neste melding i alle samtaler. Svarer besøkende på norsk brukes den norske, ellers den engelske.</p>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        {[["no", "Norsk"], ["en", "English"]].map(([k, l]) => (
          <button key={k} data-testid={`prompt-lang-${k}`} onClick={() => switchLang(k)}
            className={`${btn} ${lang === k ? "border-nettred text-coolwhite" : "border-edge text-dim hover:text-coolwhite"}`}>{l}</button>
        ))}
        {cur && (
          <span data-testid="prompt-status" className={`ml-2 font-term text-[12px] ${cur.is_default ? "text-dim" : "text-l3"}`}>
            {cur.is_default ? "// standardinstruks" : `// egen versjon · oppdatert ${fmt(cur.updated_at)}`}{dirty ? " · ulagrede endringer" : ""}
          </span>
        )}
      </div>
      <textarea data-testid="prompt-text" aria-label="Instruks" value={text} onChange={(e) => setText(e.target.value)} rows={26} spellCheck
        className="w-full bg-space border border-edge focus:border-l3 outline-none p-4 font-term text-[13px] leading-relaxed text-coolwhite" />
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <span className={`font-term text-[12px] ${text.length < 200 || text.length > 20000 ? "text-nettred" : "text-dim"}`}>{text.length} / 20 000 tegn (min. 200)</span>
        <div className="flex gap-2">
          <button data-testid="prompt-reset" disabled={busy || cur?.is_default} onClick={reset} className={`${btn} border-edge text-coolwhite hover:text-nettred`}>Tilbakestill til standard</button>
          <button data-testid="prompt-undo" disabled={busy || !dirty} onClick={() => setText(cur.text)} className={`${btn} border-edge text-coolwhite`}>Forkast endringer</button>
          <button data-testid="prompt-save" disabled={busy || !dirty || text.length < 200} onClick={save} className={`${btn} bg-nettred border-nettred text-space hover:bg-amber hover:border-amber`}>{busy ? "…" : "Lagre"}</button>
        </div>
      </div>
      {cur && (
        <div data-testid="prompt-booking-rule" className="border border-edge p-4">
          <div className="font-display tracking-[0.18em] text-[11px] uppercase text-coolwhite">Fast teknisk regel (legges alltid til, kan ikke redigeres)</div>
          <p className="mt-2 font-term text-[12px] text-dim whitespace-pre-wrap">{cur.booking_rule}</p>
        </div>
      )}
      <Versions items={versions} onLoad={(t) => { setText(t); toast("Versjonen er hentet inn — trykk Lagre for å bruke den."); }} />
    </div>
  );
};
